import { configs } from "../configs";

export interface MarketAwarePrice {
  amount: string;
  currencyCode: string;
}

export interface MarketAwareProductPricing {
  price: MarketAwarePrice;
  compareAtPrice: MarketAwarePrice | null;
}

const PRICES_QUERY = `
  query MarketAwarePrices($ids: [ID!]!, $country: CountryCode!) @inContext(country: $country) {
    nodes(ids: $ids) {
      ... on Product {
        id
        priceRange {
          minVariantPrice { amount currencyCode }
        }
        compareAtPriceRange {
          minVariantPrice { amount currencyCode }
        }
      }
    }
  }
`;

const cache = new Map<string, MarketAwareProductPricing | null>();
const inflight = new Map<string, Promise<MarketAwareProductPricing | null>>();

let pendingIds = new Set<string>();
let pendingResolvers = new Map<string, Array<(p: MarketAwareProductPricing | null) => void>>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const BATCH_DELAY_MS = 30;
const BATCH_LIMIT = 50;
const SESSION_KEY_PREFIX = "bassike:mwp:";
const SESSION_TTL_MS = 30 * 60 * 1000;

interface SessionEntry {
  pricing: MarketAwareProductPricing | null;
  ts: number;
}

const sessionKey = (id: string, countryCode: string): string =>
  `${SESSION_KEY_PREFIX}${countryCode}:${id}`;

const readSession = (id: string, countryCode: string): { hit: boolean; value: MarketAwareProductPricing | null } => {
  try {
    const raw = sessionStorage.getItem(sessionKey(id, countryCode));
    if (!raw) return { hit: false, value: null };
    const entry = JSON.parse(raw) as SessionEntry;
    if (!entry || typeof entry.ts !== "number") return { hit: false, value: null };
    if (Date.now() - entry.ts > SESSION_TTL_MS) {
      sessionStorage.removeItem(sessionKey(id, countryCode));
      return { hit: false, value: null };
    }
    return { hit: true, value: entry.pricing };
  } catch {
    return { hit: false, value: null };
  }
};

const writeSession = (id: string, countryCode: string, pricing: MarketAwareProductPricing | null): void => {
  try {
    sessionStorage.setItem(
      sessionKey(id, countryCode),
      JSON.stringify({ pricing, ts: Date.now() } as SessionEntry)
    );
  } catch {
    // sessionStorage disabled or quota exceeded — degrade to in-memory only
  }
};

export const peekCachedPrice = (
  productId: string | number,
  countryCode: string
): { hit: boolean; value: MarketAwareProductPricing | null } => {
  const id = String(productId);
  if (cache.has(id)) {
    return { hit: true, value: cache.get(id) ?? null };
  }
  const session = readSession(id, countryCode);
  if (session.hit) {
    cache.set(id, session.value);
  }
  return session;
};

const toGid = (productId: string): string => {
  if (productId.startsWith("gid://")) {
    return productId;
  }
  const numericId = productId.split("/").pop();
  return `gid://shopify/Product/${numericId}`;
};

const flush = async (countryCode: string) => {
  flushTimer = null;
  if (pendingIds.size === 0) {
    return;
  }

  const ids = Array.from(pendingIds).slice(0, BATCH_LIMIT);
  ids.forEach((id) => pendingIds.delete(id));
  const resolversForBatch = new Map<string, Array<(p: MarketAwareProductPricing | null) => void>>();
  ids.forEach((id) => {
    const resolvers = pendingResolvers.get(id);
    if (resolvers) {
      resolversForBatch.set(id, resolvers);
      pendingResolvers.delete(id);
    }
  });

  if (pendingIds.size > 0) {
    flushTimer = setTimeout(() => flush(countryCode), 0);
  }

  const gids = ids.map(toGid);
  try {
    const endpoint = `https://${configs.storeDomain}/api/2024-07/graphql.json`;
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": configs.storefrontAccessToken,
      },
      body: JSON.stringify({
        query: PRICES_QUERY,
        variables: { ids: gids, country: countryCode },
      }),
    });

    const json = await response.json();
    const nodes: Array<any> = json?.data?.nodes ?? [];

    ids.forEach((id, index) => {
      const node = nodes[index];
      let pricing: MarketAwareProductPricing | null = null;
      if (node && node.priceRange?.minVariantPrice) {
        const min = node.priceRange.minVariantPrice;
        const compareMin = node.compareAtPriceRange?.minVariantPrice;
        const hasCompare = compareMin && parseFloat(compareMin.amount) > parseFloat(min.amount);
        pricing = {
          price: { amount: min.amount, currencyCode: min.currencyCode },
          compareAtPrice: hasCompare ? { amount: compareMin.amount, currencyCode: compareMin.currencyCode } : null,
        };
      }
      cache.set(id, pricing);
      writeSession(id, countryCode, pricing);
      const resolvers = resolversForBatch.get(id);
      resolvers?.forEach((resolve) => resolve(pricing));
      inflight.delete(id);
    });
  } catch (error) {
    ids.forEach((id) => {
      const resolvers = resolversForBatch.get(id);
      resolvers?.forEach((resolve) => resolve(null));
      inflight.delete(id);
    });
  }
};

export const fetchMarketAwarePrice = (
  productId: string,
  countryCode: string
): Promise<MarketAwareProductPricing | null> => {
  const id = String(productId);
  const cached = peekCachedPrice(id, countryCode);
  if (cached.hit) {
    return Promise.resolve(cached.value);
  }
  const existing = inflight.get(id);
  if (existing) {
    return existing;
  }
  const promise = new Promise<MarketAwareProductPricing | null>((resolve) => {
    const resolvers = pendingResolvers.get(id) ?? [];
    resolvers.push(resolve);
    pendingResolvers.set(id, resolvers);
    pendingIds.add(id);
    if (!flushTimer) {
      flushTimer = setTimeout(() => flush(countryCode), BATCH_DELAY_MS);
    }
  });
  inflight.set(id, promise);
  return promise;
};

const formatAmountWithCommas = (amount: string): string => {
  const [intPart, decPart] = amount.split(".");
  const intNum = parseInt(intPart, 10);
  if (!Number.isFinite(intNum)) {
    return amount;
  }
  const sign = intPart.startsWith("-") ? "-" : "";
  const intWithCommas = sign + Math.abs(intNum).toLocaleString("en-US");
  return decPart !== undefined ? `${intWithCommas}.${decPart}` : intWithCommas;
};

export const formatMarketAwarePriceForLegacy = (price: MarketAwarePrice | null | undefined): string => {
  if (!price) {
    return "";
  }
  const formatted = formatAmountWithCommas(price.amount);
  if (price.currencyCode === "AUD") {
    return `AU$${formatted}`;
  }
  return `${price.currencyCode} ${formatted}`;
};
